import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { 
  FiChevronDown, 
  FiCalendar, 
  FiUsers, 
  FiTarget, 
  FiLayers, 
  FiClock,
  FiSave,
  FiEdit3,
  FiInfo,
  FiBarChart2,
  FiFlag,
  FiDollarSign,
  FiMessageSquare,
  FiHash
} from 'react-icons/fi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';

const TaskDetailModal = React.memo(({ isOpen, onClose, task, onUpdateTask }) => {
  const modalRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const [localTask, setLocalTask] = useState(task);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Static options with useMemo for performance
  const options = useMemo(() => ({
    predecessor: ["Task-1", "Task-2", "Task-3", "Task-4"],
    successor: ["Task-5", "Task-6", "Task-7", "Task-8"],
    department: ["Onsite", "Remote", "Hybrid", "Field"],
    assignedTo: ["Karthik", "Anita", "Vijay", "Priya"],
    priority: ["Low", "Medium", "High", "Critical"],
    status: ["Not Started", "In Progress", "Completed", "On Hold", "Blocked"]
  }), []);

  // Reset local state when task changes
  useEffect(() => {
    setLocalTask(task);
    setHasChanges(false);
    setValidationErrors({});
  }, [task]);

  // Validation logic
  const validateTask = useCallback((taskData) => {
    const errors = {};
    
    if (!taskData.taskName?.trim()) {
      errors.taskName = 'Task name is required';
    }
    
    if (!taskData.category?.trim()) {
      errors.category = 'Category is required';
    }
    
    if (taskData.startDate && taskData.endDate && 
        new Date(taskData.startDate) > new Date(taskData.endDate)) {
      errors.endDate = 'End date must be after start date';
    }
    
    if (taskData.budget && isNaN(Number(taskData.budget))) {
      errors.budget = 'Budget must be a valid number';
    }
    
    return errors;
  }, []);

  // Optimized click outside handler
  const handleClickOutside = useCallback((event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      if (hasChanges) {
        if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          onClose();
        }
      } else {
        onClose();
      }
    }
  }, [onClose, hasChanges]);

  // Escape key handler
  const handleEscapeKey = useCallback((event) => {
    if (event.key === 'Escape') {
      if (hasChanges) {
        if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          onClose();
        }
      } else {
        onClose();
      }
    }
  }, [onClose, hasChanges]);

  // Event listeners with cleanup
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClickOutside, handleEscapeKey]);

  // Optimized input change handler with debouncing
  const handleInputChange = useCallback((field, value) => {
    const updatedTask = { ...localTask, [field]: value };
    setLocalTask(updatedTask);
    setHasChanges(true);
    
    // Real-time validation
    const errors = validateTask(updatedTask);
    setValidationErrors(errors);
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Auto-save with debouncing (optional)
    if (Object.keys(errors).length === 0) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        onUpdateTask(updatedTask);
        setHasChanges(false);
        autoSaveTimeoutRef.current = null;
      }, 500);
    }
  }, [localTask, onUpdateTask, validateTask]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Array field toggle with optimization
  const handleArrayFieldToggle = useCallback((field, option) => {
    const currentArray = localTask[field] || [];
    const isSelected = currentArray.includes(option);
    
    const newArray = isSelected 
      ? currentArray.filter(item => item !== option)
      : [...currentArray, option];
    
    handleInputChange(field, newArray);
  }, [localTask, handleInputChange]);

  // Dropdown controls
  const toggleDropdown = useCallback((dropdownName) => {
    setActiveDropdown(prev => prev === dropdownName ? null : dropdownName);
  }, []);

  const closeDropdowns = useCallback(() => {
    setActiveDropdown(null);
  }, []);

  // Progress color calculation
  const getProgressColor = useMemo(() => {
    return (progress) => {
      const progressNum = parseInt(progress, 10);
      if (progressNum === 100) return { color: "rgb(34, 197, 94)", bg: "rgb(240, 253, 244)" };
      if (progressNum >= 75) return { color: "rgb(34, 197, 94)", bg: "rgb(240, 253, 244)" };
      if (progressNum >= 50) return { color: "rgb(251, 146, 60)", bg: "rgb(255, 251, 235)" };
      if (progressNum >= 25) return { color: "rgb(251, 146, 60)", bg: "rgb(255, 251, 235)" };
      return { color: "rgb(244, 63, 94)", bg: "rgb(254, 242, 242)" };
    };
  }, []);

  // Priority color mapping
  const getPriorityColor = useMemo(() => {
    return (priority) => {
      switch (priority) {
        case 'Critical': return { color: '#dc2626', bg: '#fef2f2' };
        case 'High': return { color: '#ea580c', bg: '#fff7ed' };
        case 'Medium': return { color: '#d97706', bg: '#fffbeb' };
        case 'Low': return { color: '#059669', bg: '#ecfdf5' };
        default: return { color: '#6b7280', bg: '#f9fafb' };
      }
    };
  }, []);

  // Status color mapping
  const getStatusColor = useMemo(() => {
    return (status) => {
      switch (status) {
        case 'Completed': return { color: '#059669', bg: '#ecfdf5' };
        case 'In Progress': return { color: '#2563eb', bg: '#eff6ff' };
        case 'On Hold': return { color: '#d97706', bg: '#fffbeb' };
        case 'Blocked': return { color: '#dc2626', bg: '#fef2f2' };
        case 'Not Started': return { color: '#6b7280', bg: '#f9fafb' };
        default: return { color: '#6b7280', bg: '#f9fafb' };
      }
    };
  }, []);

  // Save handler
  const handleSave = useCallback(async () => {
    const errors = validateTask(localTask);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      await onUpdateTask(localTask);
      setHasChanges(false);
      setValidationErrors({});
    } catch (error) {
      console.error('Error saving task:', error);
    } finally {
      setIsLoading(false);
    }
  }, [localTask, onUpdateTask, validateTask]);

  // Enhanced Dropdown component
  const Dropdown = React.memo(({ name, options, value, onChange, placeholder, isMultiple = false, icon: Icon, error }) => {
    const isOpen = activeDropdown === name;
    const displayValue = isMultiple 
      ? (value?.length > 0 ? `${value.length} selected` : placeholder)
      : (value || placeholder);

    return (
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => toggleDropdown(name)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${error ? '#dc2626' : isOpen ? '#3b82f6' : '#d1d5db'}`,
            borderRadius: '8px',
            background: '#ffffff',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            transition: 'all 0.2s ease',
            textAlign: 'left',
            boxShadow: isOpen ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon size={16} style={{ color: error ? '#dc2626' : '#6b7280' }} />
            <span style={{ color: value ? '#374151' : '#9ca3af', fontWeight: '500' }}>
              {displayValue}
            </span>
          </div>
          <FiChevronDown 
            size={16} 
            style={{ 
              color: error ? '#dc2626' : '#6b7280',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }} 
          />
        </button>
        
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: '0',
            right: '0',
            background: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
            zIndex: '30',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {options.map(option => {
              const isSelected = isMultiple 
                ? (value || []).includes(option)
                : value === option;
              
              return (
                <div
                  key={option}
                  onClick={() => {
                    if (isMultiple) {
                      handleArrayFieldToggle(name, option);
                    } else {
                      onChange(option);
                      closeDropdowns();
                    }
                  }}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.15s ease',
                    color: isSelected ? '#3b82f6' : '#374151',
                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                    fontWeight: isSelected ? '600' : '500'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.target.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  {isMultiple && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      style={{ margin: '0', width: '16px', height: '16px', accentColor: '#3b82f6' }}
                    />
                  )}
                  <span>{option}</span>
                </div>
              );
            })}
          </div>
        )}
        {error && (
          <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', fontWeight: '500' }}>
            {error}
          </div>
        )}
      </div>
    );
  });

  // Enhanced Tags component
  const Tags = React.memo(({ items = [], onRemove, maxDisplay = 5 }) => {
    const [showAll, setShowAll] = useState(false);
    const displayItems = showAll ? items : items.slice(0, maxDisplay);
    const hasMore = items.length > maxDisplay;
    
    if (items.length === 0) return null;
    
    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {displayItems.map(item => (
            <span 
              key={item} 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                color: '#1e40af',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                border: '1px solid #bfdbfe',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
            >
              <span>{item}</span>
              <button
                onClick={() => onRemove(item)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#1e40af',
                  padding: '2px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#bfdbfe'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                type="button"
              >
                <RxCross2 size={12} />
              </button>
            </span>
          ))}
        </div>
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              fontSize: '12px',
              cursor: 'pointer',
              marginTop: '4px',
              fontWeight: '500',
              textDecoration: 'underline'
            }}
          >
            {showAll ? 'Show less' : `+${items.length - maxDisplay} more`}
          </button>
        )}
      </div>
    );
  });

  // Progress ring component
  const ProgressRing = React.memo(({ progress, size = 60 }) => {
    const radius = (size - 8) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (progress / 100) * circumference;
    const progressColors = getProgressColor(progress);

    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth="4"
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={progressColors.color}
            strokeWidth="4"
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '12px',
          fontWeight: '700',
          color: progressColors.color
        }}>
          {progress}%
        </div>
      </div>
    );
  });

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: '1000',
        display: 'flex',
        justifyContent: 'flex-end',
        animation: isOpen ? 'fadeIn 0.3s ease' : undefined
      }}
    >
      <div 
        ref={modalRef}
        style={{
          width: '40%',
          minWidth: '500px',
          maxWidth: '700px',
          background: '#ffffff',
          height: '100vh',
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }}
        onClick={(e) => {
          e.stopPropagation();
          // Only close dropdowns if clicking outside input fields
          if (!e.target.closest('input, textarea')) {
            closeDropdowns();
          }
        }}
      >
        {/* Enhanced Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}>
              <FiEdit3 size={20} style={{ color: '#ffffff' }} />
            </div>
            <div>
              <h2 style={{ 
                margin: '0', 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#1e293b',
                letterSpacing: '-0.025em'
              }}>
                Task Details
              </h2>
              <p style={{ 
                margin: '2px 0 0 0', 
                fontSize: '14px', 
                color: '#64748b',
                fontWeight: '500'
              }}>
                {localTask.category} • #{localTask.serialNumber}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {hasChanges && (
              <button 
                onClick={handleSave}
                disabled={isLoading || Object.keys(validationErrors).length > 0}
                style={{
                  background: Object.keys(validationErrors).length > 0 
                    ? '#9ca3af' 
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: Object.keys(validationErrors).length > 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiSave size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            )}
            <button 
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
              type="button"
            >
              <RxCross2 size={20} />
            </button>
          </div>
        </div>

        {/* Enhanced Content with better organization */}
        <div style={{
          flex: '1',
          padding: '24px',
          overflowY: 'auto',
          fontSize: '14px',
          background: '#fafafa'
        }}>
          {/* Task Overview Card */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3 style={{
                margin: '0',
                fontSize: '18px',
                fontWeight: '700',
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FiInfo size={18} style={{ color: '#3b82f6' }} />
                Overview
              </h3>
              <ProgressRing progress={localTask.progress || 0} />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '6px', 
                display: 'block' 
              }}>
                Task Name
              </label>
              <input
                type="text"
                value={localTask.taskName || ''}
                onChange={(e) => handleInputChange('taskName', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `1px solid ${validationErrors.taskName ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease',
                  background: '#ffffff'
                }}
                placeholder="Enter task name"
              />
              {validationErrors.taskName && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', fontWeight: '500' }}>
                  {validationErrors.taskName}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#6b7280', 
                  marginBottom: '6px', 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <FiHash size={12} />
                  Serial #
                </label>
                <div style={{
                  padding: '10px 12px',
                  background: '#f8fafc',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontWeight: '600'
                }}>
                  {localTask.serialNumber}
                </div>
              </div>
              
              <div>
                <label style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#6b7280', 
                  marginBottom: '6px', 
                  display: 'block' 
                }}>
                  Category
                </label>
                <input
                  type="text"
                  value={localTask.category || ''}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${validationErrors.category ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontWeight: '500'
                  }}
                />
                {validationErrors.category && (
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                    {validationErrors.category}
                  </div>
                )}
              </div>

              <div>
                <label style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#6b7280', 
                  marginBottom: '6px', 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <FiClock size={12} />
                  Delay (days)
                </label>
                <input
                  type="number"
                  value={localTask.estimatedDelay || 0}
                  onChange={(e) => handleInputChange('estimatedDelay', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontWeight: '500'
                  }}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Schedule & Timeline Card */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FiCalendar size={16} style={{ color: '#3b82f6' }} />
              Schedule & Timeline
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '6px', 
                  display: 'block' 
                }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={localTask.startDate || ''}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontWeight: '500'
                  }}
                />
              </div>

              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '6px', 
                  display: 'block' 
                }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={localTask.endDate || ''}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: `1px solid ${validationErrors.endDate ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontWeight: '500'
                  }}
                />
                {validationErrors.endDate && (
                  <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                    {validationErrors.endDate}
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced Progress Section */}
            <div>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '8px', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiBarChart2 size={14} />
                  Progress
                </span>
                <span style={{ 
                  fontSize: '16px', 
                  fontWeight: '700',
                  color: getProgressColor(localTask.progress).color 
                }}>
                  {localTask.progress || 0}%
                </span>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={localTask.progress || 0}
                onChange={(e) => handleInputChange('progress', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  outline: 'none',
                  cursor: 'pointer',
                  background: `linear-gradient(to right, ${getProgressColor(localTask.progress).color} 0%, ${getProgressColor(localTask.progress).color} ${localTask.progress || 0}%, #e5e7eb ${localTask.progress || 0}%, #e5e7eb 100%)`,
                  appearance: 'none'
                }}
              />
            </div>
          </div>

          {/* Status & Priority Card */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FiFlag size={16} style={{ color: '#3b82f6' }} />
              Status & Priority
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '6px', 
                  display: 'block' 
                }}>
                  Priority
                </label>
                <Dropdown
                  name="priority"
                  options={options.priority}
                  value={localTask.priority}
                  onChange={(value) => handleInputChange('priority', value)}
                  placeholder="Select priority"
                  icon={FiFlag}
                />
                {localTask.priority && (
                  <div style={{
                    marginTop: '6px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textAlign: 'center',
                    ...getPriorityColor(localTask.priority)
                  }}>
                    {localTask.priority} Priority
                  </div>
                )}
              </div>

              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '6px', 
                  display: 'block' 
                }}>
                  Status
                </label>
                <Dropdown
                  name="status"
                  options={options.status}
                  value={localTask.status}
                  onChange={(value) => handleInputChange('status', value)}
                  placeholder="Select status"
                  icon={FiTarget}
                />
                {localTask.status && (
                  <div style={{
                    marginTop: '6px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textAlign: 'center',
                    ...getStatusColor(localTask.status)
                  }}>
                    {localTask.status}
                  </div>
                )}
              </div>

              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '6px', 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <FiDollarSign size={14} />
                  Budget
                </label>
                <input
                  type="number"
                  value={localTask.budget || ''}
                  onChange={(e) => handleInputChange('budget', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${validationErrors.budget ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontWeight: '500'
                  }}
                  placeholder="0"
                />
                {validationErrors.budget && (
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                    {validationErrors.budget}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dependencies Card */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FiLayers size={16} style={{ color: '#3b82f6' }} />
              Dependencies
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '8px', 
                  display: 'block' 
                }}>
                  Predecessors
                </label>
                <Dropdown
                  name="predecessor"
                  options={options.predecessor}
                  value={localTask.predecessor}
                  onChange={() => {}}
                  placeholder="Select predecessors"
                  isMultiple={true}
                  icon={FiLayers}
                />
                <Tags 
                  items={localTask.predecessor} 
                  onRemove={(item) => handleArrayFieldToggle('predecessor', item)}
                />
              </div>

              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '8px', 
                  display: 'block' 
                }}>
                  Successors
                </label>
                <Dropdown
                  name="successor"
                  options={options.successor}
                  value={localTask.successor}
                  onChange={() => {}}
                  placeholder="Select successors"
                  isMultiple={true}
                  icon={FiLayers}
                />
                <Tags 
                  items={localTask.successor} 
                  onRemove={(item) => handleArrayFieldToggle('successor', item)}
                />
              </div>
            </div>
          </div>

          {/* Assignment Card */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FiUsers size={16} style={{ color: '#3b82f6' }} />
              Team Assignment
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '8px', 
                  display: 'block' 
                }}>
                  Department
                </label>
                <Dropdown
                  name="department"
                  options={options.department}
                  value={localTask.department}
                  onChange={(value) => handleInputChange('department', value)}
                  placeholder="Select department"
                  icon={HiOutlineOfficeBuilding}
                />
              </div>

              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '8px', 
                  display: 'block' 
                }}>
                  Assigned Members
                </label>
                <Dropdown
                  name="assignedTo"
                  options={options.assignedTo}
                  value={localTask.assignedTo}
                  onChange={() => {}}
                  placeholder="Assign members"
                  isMultiple={true}
                  icon={FiUsers}
                />
                <Tags 
                  items={localTask.assignedTo} 
                  onRemove={(item) => handleArrayFieldToggle('assignedTo', item)}
                />
              </div>
            </div>
          </div>

          {/* Notes Card */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FiMessageSquare size={16} style={{ color: '#3b82f6' }} />
              Notes & Comments
            </h3>
            <textarea
              value={localTask.notes || ''}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                minHeight: '100px',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                background: '#ffffff'
              }}
              placeholder="Add notes, comments, or additional details..."
            />
          </div>
        </div>
      </div>
    </div>
  );
});

TaskDetailModal.displayName = 'TaskDetailModal';

export default TaskDetailModal;